import { TrashCIcon } from '@prophecy/icons';
import { NestedData } from '@prophecy/interfaces/generic';
import { ExcelTable, Input, Table } from '@prophecy/ui-v3';
import React, { useEffect } from 'react';
import { useImmer } from 'use-immer';

import { UserMatcherTypeWithIndex } from './types';

const columns = (
  onChange: (field: UserMatcherTypeWithIndex, index: number, isDelete?: boolean) => void,
  matchersCount: number
) => [
  {
    title: 'User Attributes',
    dataIndex: 'userAttr',
    key: 'userAttr',
    render: (value: string, record: NestedData, index: number) => (
      <Input
        value={value}
        inputSize='s'
        variant={value || matchersCount === index ? 'primary' : 'error'}
        onChange={(userAttr) => onChange({ ...record, userAttr } as UserMatcherTypeWithIndex, index)}
        data-test-id='user-attribute-user-attributes-input'
      />
    )
  },
  {
    title: 'Group Attributes',
    dataIndex: 'groupAttr',
    key: 'groupAttr',
    render: (value: string, record: NestedData, index: number) => (
      <Input
        value={value}
        inputSize='s'
        variant={value || matchersCount === index ? 'primary' : 'error'}
        onChange={(groupAttr) => onChange({ ...record, groupAttr } as UserMatcherTypeWithIndex, index)}
        data-test-id='user-attribute-group-attributes-input'
      />
    )
  },
  {
    title: '',
    dataIndex: 'actions',
    key: 'actions',
    resizable: false,
    render: (_: string, record: NestedData, index: number) => {
      if (matchersCount === index) return null;
      return (
        <Table.IconButton
          onClick={() => onChange(record as UserMatcherTypeWithIndex, index, true)}
          icon={<TrashCIcon type='default' size='xs' />}
          data-test-id='user-attribute-delete-button'
          aria-label='Delete attribute'
        />
      );
    }
  }
];

const getUserMatchersDataWithIndex = (data: UserMatcherTypeWithIndex[]) => {
  return data.map((value, i) => ({ ...value, key: value?.key || `${value.userAttr}[${i}]` }));
};

export const UserAttribute = ({
  tableData,
  updateUserMatchersData
}: {
  tableData: UserMatcherTypeWithIndex[];
  updateUserMatchersData: (data: UserMatcherTypeWithIndex[]) => void;
}) => {
  const [userMatchers, setUserMatchers] = useImmer<UserMatcherTypeWithIndex[]>(getUserMatchersDataWithIndex(tableData));
  useEffect(() => {
    setUserMatchers(getUserMatchersDataWithIndex(tableData));
  }, [tableData, setUserMatchers]);

  const getNewItem = () => {
    return { userAttr: '', key: `${userMatchers.length}__attr`, groupAttr: '' };
  };

  useEffect(() => {
    updateUserMatchersData?.(userMatchers);
  }, [updateUserMatchersData, userMatchers]);

  const onChange = (field: UserMatcherTypeWithIndex, index: number, isDelete?: boolean) => {
    setUserMatchers((draft) => {
      if (index === draft.length) {
        draft.push(field);
      }
      draft[index] = field;
      if (isDelete) {
        draft.splice(index, 1);
      }
    });
  };

  return (
    <ExcelTable
      width='100%'
      height='100%'
      buildNewRow={() => getNewItem()}
      placeholderRows={tableData?.length > 10 ? 0 : 3}
      columns={columns(onChange, userMatchers.length)}
      dataSource={userMatchers}
      data-test-id='auth-provider-user-attributes-table'
    />
  );
};
